import React, { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import { Avatar, Paper } from '@mui/material';
import {
  GLOW_OFF_OTHER_WINNERS_AVATAR,
  GLOW_OFF_WINNER_AVATAR,
  GLOW_ON_OTHER_WINNERS_AVATAR,
  GLOW_ON_WINNER_AVATAR,
  INSET_OTHER_WINNERS_AVATAR,
  INSET_WINNER_AVATAR
} from 'constants/gameConstants';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import GameContext from 'contexts/GameContext';
import GlobalContext from 'contexts/GlobalContext';
import { useGridMode } from 'contexts/GridModeContext';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import { useIsActiveTable } from 'contexts/TableActiveContext';
import { TableIdContext } from 'contexts/TableIdContext';
import gsap from 'gsap';
import { useTableSelector } from 'hooks/useTableSelector';
import { has } from 'lodash';

import SeatFrame from 'components/Streaks/SeatFrame';
import { FrameTier } from 'components/Streaks/model/types';

import { claimBustedPrompt } from './bustedPrompts';

type PlayerPropType = {
  avatar: string;
  seatId: number;
  currentPlayerSeatId: number;
  isCurrentWinner: boolean;
  /** EN-11164: the streak frame outside the avatar; null for none. */
  streakFrame?: FrameTier | null;
  hideStreakFrame?: boolean;
};

const Player = ({
  seatId,
  avatar,
  currentPlayerSeatId,
  isCurrentWinner,
  streakFrame = null,
  hideStreakFrame = false
}: PlayerPropType) => {
  const { reduceAnimations } = useAnimationPreferences();
  const { setOpenAddChips, isSatOut, setIsTimerOff } = useContext(GameContext);
  const { setCloseBuyInModal } = useContext(GlobalContext);
  const tableId = useContext(TableIdContext);
  const isActiveTable = useIsActiveTable();
  const gridMode = useGridMode();

  const game = useTableSelector((table) => table.game);
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const glowRef = useRef<HTMLDivElement>(null);

  const [status, setStatus] = useState<string>('');
  const [isShowStatus, setIsShowStatus] = useState<boolean>(false);

  const insetShadowColor = isCurrentWinner ? INSET_WINNER_AVATAR : INSET_OTHER_WINNERS_AVATAR;
  const shadowColorHide = isCurrentWinner ? GLOW_OFF_WINNER_AVATAR : GLOW_OFF_OTHER_WINNERS_AVATAR;
  const shadowColorShow = isCurrentWinner ? GLOW_ON_WINNER_AVATAR : GLOW_ON_OTHER_WINNERS_AVATAR;

  useEffect(() => {
    const el = glowRef.current;
    if (!el) return;
    const target = isCurrentWinner
      ? `inset 0px 0px 0px 5px ${insetShadowColor}, 0px 0px 10px 10px ${shadowColorShow}`
      : `inset 0px 0px 0px 0px ${insetShadowColor}, 0px 0px 0px 0px ${shadowColorHide}`;

    if (isHandHistorySnapshot) {
      gsap.set(el, { boxShadow: target });
      return;
    }

    if (reduceAnimations) {
      gsap.set(el, { boxShadow: target });
    } else if (isCurrentWinner) {
      // Winner celebration glow
      const tl = gsap.timeline();
      tl.to(glowRef.current, {
        boxShadow: `inset 0px 0px 0px 5px ${insetShadowColor}, 0px 0px 15px 12px ${shadowColorShow}`,
        duration: 0.4,
        ease: 'power2.out'
      }).to(glowRef.current, {
        boxShadow: `inset 0px 0px 0px 3px ${insetShadowColor}, 0px 0px 8px 6px ${shadowColorShow}`,
        duration: 0.5,
        ease: 'sine.inOut',
        repeat: 3,
        yoyo: true
      });
    } else {
      gsap.to(glowRef.current, {
        boxShadow: target,
        duration: 0.6,
        ease: 'power2.out'
      });
    }

    // Kill the untracked glow timeline/tween on re-run/unmount so a seat that
    // unmounts mid-glow doesn't orphan it against a detached avatar node.
    return () => {
      gsap.killTweensOf(el);
    };
  }, [
    isCurrentWinner,
    insetShadowColor,
    isHandHistorySnapshot,
    shadowColorShow,
    shadowColorHide,
    reduceAnimations
  ]);

  useEffect(() => {
    switch (game['p' + seatId + 'status']) {
      case 0:
        setIsShowStatus(false);
        setStatus('');
        break;
      case 1:
        setIsShowStatus(true);
        setStatus('SITTING OUT');
        break;
      case 6:
        setIsShowStatus(seatId === currentPlayerSeatId);
        setStatus('SITTING OUT');
        break;
      case 2:
        setIsShowStatus(true);
        setStatus('STANDING UP');
        break;
      case 8:
        setIsShowStatus(true);
        setStatus('SITTING OUT');
        break;
      case 3:
        setStatus('');
        break;
      case 4:
        setIsShowStatus(true);
        setStatus('JOINING');
        break;
      case 5:
        setIsShowStatus(true);
        setStatus('JOINING');
        break;
      case 10:
      case 17:
      case 18:
        setIsShowStatus(true);
        setStatus('LEFT GAME');
        break;
      case 12:
        // The broadcaster can omit `bust` on repeated busted-pause frames.
        // Claim the hand once across provider remounts, independently of that
        // optional countdown payload, so rejoining cannot reopen the prompt.
        if (
          (gridMode || isActiveTable) &&
          currentPlayerSeatId === seatId &&
          game['hand'] === '' &&
          game['languageKey'] === 'GAME_MSG_BUSTED_PAUSE' &&
          claimBustedPrompt(tableId || game['gameID'], game[`p${seatId}name`], game['gameNo'])
        ) {
          // A previous countdown/cancel must not hide a new bust window.
          // Hidden carousel panels wait until active before consuming it;
          // every visible grid cell may prompt, regardless of audio focus.
          setCloseBuyInModal(false);
          setOpenAddChips(true);
          if (has(game['bust'], 'pauseStartTime')) {
            setIsTimerOff(true);
          }
        }
        break;
      default:
        break;
    }
  }, [
    game,
    seatId,
    currentPlayerSeatId,
    isSatOut,
    tableId,
    gridMode,
    isActiveTable,
    setCloseBuyInModal,
    setOpenAddChips,
    setIsTimerOff
  ]);

  return (
    <Paper
      style={{
        position: 'relative',
        height: '100%',
        width: '100%',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      className='avatar-container'>
      {/* data-seat-avatar: the multi-run vote's felt state dims or lights it (EN-10856). */}
      <Avatar sx={avatarStyle} src={avatar} data-seat-avatar=''></Avatar>
      <div
        ref={glowRef}
        style={{
          position: 'absolute',
          borderRadius: '50%',
          width: '100%',
          height: '100%'
        }}></div>
      <SeatFrame tier={streakFrame} hidden={hideStreakFrame} />
      {isShowStatus && (
        <>
          <div style={coverStyle}></div>
          <div style={coverTextStyle}>{status}</div>
        </>
      )}
    </Paper>
  );
};

export default Player;

const baseStyle: CSSProperties = {
  position: 'absolute',
  height: '100%',
  width: '100%',
  borderRadius: '50%'
};

const avatarStyle: CSSProperties = { ...baseStyle };

const coverStyle: CSSProperties = {
  ...baseStyle,
  backgroundColor: 'black',
  opacity: 0.75,
  transition: 'all 1s ease'
};

const coverTextStyle: CSSProperties = {
  ...baseStyle,
  fontSize: '.70rem',
  color: '#FFF',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 600,
  textAlign: 'center'
};
